import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wrench, Plus, Loader2, Building2, DoorClosed, User, AlertTriangle, Sparkles, FileUp, Trash2, ListChecks } from "lucide-react";
import { maintenanceApi, engineersApi, buildingsApi, doorsApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { fmtDate, apiError, statusTone, prettyStatus, fmtMoney } from "@/lib/format";
import { useAuth, userHasRole } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import UpgradeBanner from "@/components/UpgradeBanner";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In progress" },
  { key: "completed", label: "Completed" },
];

const PRIORITIES = ["urgent", "normal", "low"];

function AdHocDialog({ open, onOpenChange, onCreated }) {
  const buildings = useAsync(() => (open ? buildingsApi.getAll() : Promise.resolve({ data: [] })), [open]);
  const engineers = useAsync(() => (open ? engineersApi.list() : Promise.resolve({ data: [] })), [open]);
  const [buildingId, setBuildingId] = useState("");
  const [doors, setDoors] = useState([]);
  const [form, setForm] = useState({ door_id: "", title: "", priority: "normal", engineer_id: "", notes: "" });
  const [saving, setSaving] = useState(false);
  // Iter-108y — multi-part job items: each becomes a checklist entry the
  // engineer photographs individually on completion. Prefilled from the
  // AI extraction's suggested_parts; also manually add/edit/remove.
  const [parts, setParts] = useState([]); // [{ component, description, cost, quantity }]
  const addPart = () => setParts((p) => [...p, { uid: Math.random().toString(36).slice(2), component: "", description: "", cost: "", quantity: 1, autoPriced: false }]);
  const updatePart = (idx, patch) => setParts((p) => p.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  const removePart = (idx) => setParts((p) => p.filter((_, i) => i !== idx));
  const partsTotal = parts.reduce((s, r) => s + (parseFloat(r.cost) || 0) * (parseInt(r.quantity, 10) || 1), 0);
  // AI extract-from-file state
  const [extracting, setExtracting] = useState(false);
  const [creatingBuilding, setCreatingBuilding] = useState(false);
  const [extracted, setExtracted] = useState(null); // { file_hash, external_quote_ref, external_quote_notes, external_quote_pdf, extracted_doors, extracted_building, suggested_building, duplicate_matches, counts }
  const [overrideDup, setOverrideDup] = useState(false);
  const fileRef = React.useRef(null);

  const resetAll = () => {
    setBuildingId(""); setDoors([]);
    setForm({ door_id: "", title: "", priority: "normal", engineer_id: "", notes: "" });
    setParts([]);
    setExtracted(null); setOverrideDup(false);
  };

  React.useEffect(() => {
    if (!open) resetAll();
  }, [open]);

  const pickBuilding = async (bid) => {
    setBuildingId(bid);
    setForm((f) => ({ ...f, door_id: "" }));
    try { const r = await doorsApi.getAll(bid); setDoors(Array.isArray(r.data) ? r.data : []); }
    catch { setDoors([]); }
  };

  const handleExtractFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await maintenanceApi.extractFromFile(fd);
      const d = res.data || {};
      const dup = Array.isArray(d.duplicate_matches) ? d.duplicate_matches : [];
      setExtracted({
        file_hash: d.file_hash || null,
        external_quote_ref: d.external_quote_ref || null,
        external_quote_notes: d.external_quote_notes || null,
        external_quote_pdf: d.file_base64 || null,
        extracted_doors: Array.isArray(d.suggested_doors) ? d.suggested_doors : [],
        extracted_building: d.extracted_building || null,
        suggested_building: d.suggested_building || null,
        duplicate_matches: dup,
        counts: { doors: (d.suggested_doors || []).length, parts: (d.suggested_parts || []).length },
      });
      setOverrideDup(false);
      setForm((f) => ({
        ...f,
        title: (d.suggested_title || f.title || "").slice(0, 200),
        notes: (d.suggested_notes || f.notes || ""),
      }));
      // Prefill the parts checklist from the extracted per-defect parts,
      // merging onto any rows the admin already added.
      const sp = Array.isArray(d.suggested_parts) ? d.suggested_parts : [];
      if (sp.length) {
        setParts((prev) => [
          ...prev.filter((r) => r.component.trim()),
          ...sp.map((p) => ({ uid: Math.random().toString(36).slice(2), component: p.component || "", description: p.description || "", cost: p.cost ? String(p.cost) : "", quantity: p.quantity || 1, autoPriced: !!p.priced_from_library })),
        ]);
      }
      if (d.suggested_building?.id) {
        await pickBuilding(d.suggested_building.id);
      }
      const dCount = (d.suggested_doors || []).length;
      const pCount = sp.length;
      toast.success(`Extracted ${dCount} door${dCount === 1 ? "" : "s"} · ${pCount} part${pCount === 1 ? "" : "s"} — review and create the job.${dup.length ? " Possible duplicate found." : ""}`);
    } catch (err) {
      toast.error(apiError(err, "Could not read that file"));
    } finally {
      setExtracting(false);
    }
  };

  const handleCreateExtractedBuilding = async () => {
    const eb = extracted?.extracted_building;
    if (!eb?.name) return;
    setCreatingBuilding(true);
    try {
      const res = await buildingsApi.create({ name: eb.name, address: eb.address || "", postcode: eb.postcode || "" });
      const newId = res.data?.id;
      await buildings.reload?.();
      if (newId) { await pickBuilding(newId); toast.success(`Building “${eb.name}” created`); }
    } catch (e) {
      toast.error(apiError(e, "Could not create building"));
    } finally {
      setCreatingBuilding(false);
    }
  };

  const dupBlocked = !!extracted && extracted.duplicate_matches.length > 0 && !overrideDup;

  const submit = async () => {
    if (!buildingId) { toast.error("Choose a building."); return; }
    if (!form.title.trim()) { toast.error("Enter a job title."); return; }
    if (dupBlocked) { toast.error("This looks like a duplicate — tick “Import as new anyway” to continue."); return; }
    setSaving(true);
    try {
      const payload = {
        building_id: buildingId,
        door_id: form.door_id || null,
        title: form.title.trim(),
        priority: form.priority,
        notes: form.notes.trim() || null,
        engineer_id: form.engineer_id || null,
      };
      const items = parts
        .map((p) => ({
          component: (p.component || "").trim(),
          description: (p.description || "").trim(),
          cost: parseFloat(p.cost) || 0,
          quantity: parseInt(p.quantity, 10) || 1,
        }))
        .filter((p) => p.component.length > 0);
      if (items.length) payload.items = items;
      if (extracted) {
        payload.extracted_doors = extracted.extracted_doors;
        payload.external_quote_pdf = extracted.external_quote_pdf;
        payload.external_quote_ref = extracted.external_quote_ref;
        payload.external_quote_notes = extracted.external_quote_notes;
        payload.external_quote_hash = extracted.file_hash;
        payload.duplicate_override = extracted.duplicate_matches.length > 0 ? overrideDup : false;
      }
      const res = await maintenanceApi.createAdHoc(payload);
      toast.success("Maintenance job created");
      onCreated(res.data);
    } catch (e) { toast.error(apiError(e, "Could not create job")); }
    finally { setSaving(false); }
  };

  const bldList = Array.isArray(buildings.data) ? buildings.data : [];
  const engList = Array.isArray(engineers.data) ? engineers.data : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New maintenance job</DialogTitle>
          <DialogDescription>Create a reactive job, or import one from a supplier quote / inspection report with AI.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* AI import */}
          <div className="rounded-lg border border-dashed border-primary/30 bg-primary/5 p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Sparkles className="h-4 w-4 text-primary" /> Import from file (AI)
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={extracting} data-testid="adhoc-extract-btn">
                {extracting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileUp className="mr-2 h-4 w-4" />}
                {extracting ? "Reading…" : "Upload report"}
              </Button>
            </div>
            <p className="mt-1 text-xs text-slate-500">PDF, Word, Excel or a photo (JPG/PNG). We’ll prefill the building, title, notes and doors.</p>
            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.heic" className="hidden" onChange={handleExtractFile} data-testid="adhoc-extract-input" />

            {extracted && (
              <div className="mt-3 space-y-2 rounded-md bg-white/70 p-2.5 text-xs" data-testid="adhoc-extract-summary">
                <p className="font-semibold text-emerald-700">
                  ✓ Extracted {extracted.counts.doors} door{extracted.counts.doors === 1 ? "" : "s"}
                  {extracted.external_quote_ref ? ` · Ref ${extracted.external_quote_ref}` : ""}
                </p>
                {extracted.suggested_building ? (
                  <p className="text-slate-600">Matched building: <span className="font-medium">{extracted.suggested_building.name}</span></p>
                ) : extracted.extracted_building?.name ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-slate-600">No match for “{extracted.extracted_building.name}”.</span>
                    <Button type="button" size="sm" variant="outline" className="h-7" onClick={handleCreateExtractedBuilding} disabled={creatingBuilding} data-testid="adhoc-create-building-btn">
                      {creatingBuilding ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-1.5 h-3.5 w-3.5" />}
                      Create it
                    </Button>
                    <span className="text-slate-500">or pick one below.</span>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Duplicate warning */}
          {extracted && extracted.duplicate_matches.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="adhoc-dup-warning">
              <p className="flex items-center gap-1.5 font-semibold text-amber-800"><AlertTriangle className="h-4 w-4" /> Possible duplicate import</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-amber-800">
                {extracted.duplicate_matches.slice(0, 3).map((m) => (
                  <li key={m.job_id}>{m.title} <span className="text-amber-600">({m.matched_by})</span></li>
                ))}
              </ul>
              <label className="mt-2 flex items-center gap-2 font-medium text-amber-900">
                <input type="checkbox" checked={overrideDup} onChange={(e) => setOverrideDup(e.target.checked)} data-testid="adhoc-dup-override" />
                Import as new anyway
              </label>
            </div>
          )}

          <div className="space-y-2">
            <Label>Building *</Label>
            <Select value={buildingId} onValueChange={pickBuilding}>
              <SelectTrigger data-testid="adhoc-building-select"><SelectValue placeholder="Select building" /></SelectTrigger>
              <SelectContent>
                {bldList.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {doors.length > 0 && (
            <div className="space-y-2">
              <Label>Door (optional)</Label>
              <Select value={form.door_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, door_id: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="adhoc-door-select"><SelectValue placeholder="Building-wide" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Building-wide / no door</SelectItem>
                  {doors.map((d) => <SelectItem key={d.id} value={d.id}>{d.door_reference}{d.location ? ` · ${d.location}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label>Job title *</Label>
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Replace door closer" data-testid="adhoc-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                <SelectTrigger data-testid="adhoc-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Assign engineer</Label>
              <Select value={form.engineer_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, engineer_id: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="adhoc-engineer-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {engList.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="adhoc-notes-input" />
          </div>

          {/* Parts checklist — each part becomes a job item the engineer photographs individually */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5"><ListChecks className="h-4 w-4 text-slate-500" /> Parts / work items</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addPart} data-testid="adhoc-part-add">
                <Plus className="mr-1 h-3.5 w-3.5" /> Add part
              </Button>
            </div>
            {parts.length === 0 ? (
              <p className="text-xs text-slate-400">Optional. Break the job into named parts (e.g. “Replace intumescent seals”) so the engineer photographs each one on completion. Add a cost to build a rough repair estimate.</p>
            ) : (
              <div className="space-y-2" data-testid="adhoc-parts-list">
                {parts.map((row, idx) => (
                  <div key={row.uid || idx} className="flex items-start gap-2 rounded-md border border-slate-100 bg-slate-50 p-2" data-testid={`adhoc-part-row-${idx}`}>
                    <div className="flex-1 space-y-1.5">
                      <Input
                        value={row.component}
                        onChange={(e) => updatePart(idx, { component: e.target.value })}
                        placeholder="Part / component (e.g. Intumescent strip)"
                        data-testid={`adhoc-part-component-${idx}`}
                      />
                      <Input
                        value={row.description}
                        onChange={(e) => updatePart(idx, { description: e.target.value })}
                        placeholder="Detail (optional) — door, location, notes"
                        className="text-sm"
                        data-testid={`adhoc-part-desc-${idx}`}
                      />
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-slate-400">Qty</span>
                          <Input
                            type="number" min="1"
                            value={row.quantity}
                            onChange={(e) => updatePart(idx, { quantity: e.target.value })}
                            className="h-8 w-16 text-sm"
                            data-testid={`adhoc-part-qty-${idx}`}
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-slate-400">£ each</span>
                          <Input
                            type="number" min="0" step="0.01"
                            value={row.cost}
                            onChange={(e) => updatePart(idx, { cost: e.target.value, autoPriced: false })}
                            placeholder="0.00"
                            className="h-8 w-24 text-sm"
                            data-testid={`adhoc-part-cost-${idx}`}
                          />
                        </div>
                        {(parseFloat(row.cost) || 0) > 0 && (
                          <span className="ml-auto text-sm font-medium text-slate-600" data-testid={`adhoc-part-line-${idx}`}>
                            {fmtMoney((parseFloat(row.cost) || 0) * (parseInt(row.quantity, 10) || 1))}
                          </span>
                        )}
                      </div>
                      {row.autoPriced ? (
                        <p className="text-xs font-medium text-emerald-600" data-testid={`adhoc-part-hint-${idx}`}>✓ Auto-priced from library</p>
                      ) : (parseFloat(row.cost) || 0) > 0 ? null : (
                        <p className="text-xs font-medium text-amber-600" data-testid={`adhoc-part-hint-${idx}`}>No price match — set a price</p>
                      )}
                    </div>
                    <Button type="button" variant="ghost" size="icon" className="mt-0.5 text-red-500 hover:bg-red-50" onClick={() => removePart(idx)} data-testid={`adhoc-part-remove-${idx}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {partsTotal > 0 && (
                  <div className="flex items-center justify-between rounded-md bg-primary/5 px-3 py-2 text-sm font-semibold text-slate-800" data-testid="adhoc-parts-total">
                    <span>Estimated total</span>
                    <span>{fmtMoney(partsTotal)}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving || extracting || dupBlocked} data-testid="adhoc-save-btn">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Create job
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Maintenance() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user?.is_company_admin || userHasRole(user, "admin", "inspector");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data, loading, error, reload } = useAsync(() => maintenanceApi.list(status ? { status } : {}), [status]);

  const list = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter((j) =>
      (j.title || "").toLowerCase().includes(s) ||
      (j.building_name || "").toLowerCase().includes(s) ||
      (j.door_reference || "").toLowerCase().includes(s) ||
      (j.engineer_name || "").toLowerCase().includes(s)
    );
  }, [list, q]);

  return (
    <div>
      <PageHeader
        title="Maintenance"
        subtitle="Remedial jobs — quote to job sheet to post-work report"
        testId="maintenance-header"
        actions={canManage ? (
          <Button onClick={() => setDialogOpen(true)} data-testid="maintenance-new-btn"><Plus className="mr-1.5 h-4 w-4" /> New job</Button>
        ) : null}
      />

      <UpgradeBanner feature="maintenance" className="mb-4" title="Maintenance is a Pro-Maintenance feature">Manage remedial jobs end-to-end — quote, job sheet and post-work report.</UpgradeBanner>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setStatus(t.key)}
            data-testid={`maintenance-tab-${t.key || "all"}`}
            className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${status === t.key ? "bg-primary text-primary-foreground" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
          >
            {t.label}
          </button>
        ))}
        <Input placeholder="Search jobs…" className="ml-auto max-w-xs" value={q} onChange={(e) => setQ(e.target.value)} data-testid="maintenance-search" />
      </div>

      {loading && <LoadingState label="Loading jobs…" />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}

      {!loading && !error && (
        filtered.length === 0 ? (
          <EmptyState
            title={list.length === 0 ? "No maintenance jobs" : "No matches"}
            message={list.length === 0 ? "Convert an accepted quote into a job, or create a reactive one." : "Try a different search or status."}
            testId="maintenance-empty"
          />
        ) : (
          <div className="space-y-2" data-testid="maintenance-list">
            {filtered.map((j) => (
              <Card key={j.id} className="flex cursor-pointer items-center justify-between gap-3 p-4 transition-shadow hover:shadow-md" onClick={() => navigate(`/maintenance/${j.id}`)} data-testid={`maintenance-row-${j.id}`}>
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Wrench className="h-5 w-5" /></div>
                  <div className="min-w-0">
                    <p className="truncate font-medium text-slate-800">
                      {j.priority === "urgent" && <AlertTriangle className="mr-1 inline h-3.5 w-3.5 text-red-500" />}
                      {j.title || j.door_reference || "Maintenance job"}
                    </p>
                    <p className="flex flex-wrap items-center gap-x-3 gap-y-0.5 truncate text-sm text-slate-500">
                      {j.building_name && <span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{j.building_name}</span>}
                      {j.door_reference && <span className="inline-flex items-center gap-1"><DoorClosed className="h-3.5 w-3.5" />{j.door_reference}</span>}
                      {j.engineer_name && <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" />{j.engineer_name}</span>}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  {j.scheduled_for && <span className="hidden text-xs text-slate-400 sm:inline">{fmtDate(j.scheduled_for)}</span>}
                  <Badge variant="outline" className={statusTone(j.status)}>{prettyStatus(j.status)}</Badge>
                </div>
              </Card>
            ))}
          </div>
        )
      )}

      <AdHocDialog open={dialogOpen} onOpenChange={setDialogOpen} onCreated={(job) => { setDialogOpen(false); if (job?.id) navigate(`/maintenance/${job.id}`); else reload(); }} />
    </div>
  );
}
